import React, { useEffect, useState } from "react";
import { message as AntdMessage } from "antd";
import { FaCommentDots, FaBlog } from "react-icons/fa";
import { apiLogin } from "@/apis/auth";
import { useNavigate } from "react-router-dom";
import useLoginStore from "@/stores/loginStore";
import { getUserRoleFromToken } from "@/utils/jwt";

import { Button } from "@/components/button";
import { Checkbox, CheckboxField } from "@/components/checkbox";
import { Field, Label } from "@/components/fieldset";
import { Heading } from "@/components/heading";
import { Input } from "@/components/input";
import { Strong, Text, TextLink } from "@/components/text";
import { Logo } from "@/components/logo";

const Login2: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, setToken } = useLoginStore();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // 사용자 역할에 따라 적절한 페이지로 리다이렉트
  const redirectBasedOnRole = (token: string) => {
    const role = getUserRoleFromToken(token);

    if (role === "student") {
      navigate("/my_exams");
    } else if (role === "admin") {
      navigate("/exam_papers");
    } else {
      // 기본 경로 (역할을 판별할 수 없는 경우)
      navigate("/");
    }
  };

  const validateInputs = (): boolean => {
    let isValid = true;

    // Validate email
    if (!email) {
      setEmailError("이메일을 입력해주세요!");
      isValid = false;
    } else {
      setEmailError("");
    }

    // Validate password
    if (!password) {
      setPasswordError("비밀번호를 입력해주세요!");
      isValid = false;
    } else {
      setPasswordError("");
    }

    return isValid;
  };

  const requestLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiLogin({ email, password });

      if (response.success && response.data?.token) {
        // 토큰 저장
        setToken(response.data.token);
        AntdMessage.success("Login success!");

        // 역할에 따라 리다이렉트
        redirectBasedOnRole(response.data.token);
      }
    } catch (error: any) {
      AntdMessage.error(error.message || "로그인에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center p-4"
      style={{
        backgroundImage: "url('/bg_login.jpg')"
      }}
    >
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
        <form
          onSubmit={requestLogin}
          className="grid w-full grid-cols-1 gap-6"
        >
          <div className="text-center mb-2 font-semibold text-[#782829] leading-none">
            <div className="text-5xl">S&S</div>
            <div className="text-xl -mt-2">ACADEMY</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your Email Address"
              className="w-full px-4 py-3 bg-gray-100 border-0 rounded-md text-zinc-900 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1">{emailError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="w-full px-4 py-3 bg-gray-100 border-0 rounded-md text-zinc-900 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {passwordError && (
              <p className="text-red-500 text-sm mt-1">{passwordError}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-black text-white py-3 px-4 rounded-md font-medium hover:bg-gray-800 transition-colors ${loading ? "opacity-70" : ""}`}
          >
            Sign in
          </button>

        </form>
        
        {/* Social Media Links - Outside of form */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-center text-xs text-gray-500 mb-4">Contact & Follow us</p>
          <div className="flex justify-center space-x-8">
            <a
              href="https://pf.kakao.com/_xoxcpDG/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-gray-600 hover:text-yellow-600 transition-colors duration-200 text-sm"
            >
              <FaCommentDots size={16} />
              <span>KakaoTalk</span>
            </a>
            <a
              href="https://blog.naver.com/snsacademy2705"
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors duration-200 text-sm"
            >
              <FaBlog size={16} />
              <span>Blog</span>
            </a>
          </div>
        </div>

        {/* Announcements Section */}
        <div className="mt-6">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <ul className="space-y-2 text-xs text-gray-500">
              <li>
                <a 
                  href="https://blog.naver.com/PostView.naver?blogId=snsacademy2705&logNo=223858294782&categoryNo=13&parentCategoryNo=1&viewDate=&currentPage=&postListTopCurrentPage="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start hover:text-gray-700 transition-colors duration-200"
                >
                  <span className="mr-2 mt-0.5">•</span>
                  <span className="text-gray-500 hover:text-gray-700">[압구정 SNS어학원] Jr. English 영재반</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://blog.naver.com/PostView.naver?blogId=snsacademy2705&logNo=223767188742&categoryNo=2&parentCategoryNo=1&viewDate=&currentPage=&postListTopCurrentPage="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start hover:text-gray-700 transition-colors duration-200"
                >
                  <span className="mr-2 mt-0.5">•</span>
                  <span className="text-gray-500 hover:text-gray-700">[S&S ACADEMY] 2025 Summer Program</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://blog.naver.com/PostView.naver?blogId=snsacademy2705&logNo=223439100026&categoryNo=4&parentCategoryNo=1&viewDate=&currentPage=&postListTopCurrentPage="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start hover:text-gray-700 transition-colors duration-200"
                >
                  <span className="mr-2 mt-0.5">•</span>
                  <span className="text-gray-500 hover:text-gray-700">[압구정 에스앤에스아카데미어학원] S&S 강사진 소개</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://blog.naver.com/PostView.naver?blogId=snsacademy2705&logNo=223433930448&categoryNo=3&parentCategoryNo=1&viewDate=&currentPage=&postListTopCurrentPage="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start hover:text-gray-700 transition-colors duration-200"
                >
                  <span className="mr-2 mt-0.5">•</span>
                  <span className="text-gray-500 hover:text-gray-700">[에스앤에스어학원] 학원 오시는 길 압구정역</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login2; 